import { ChangeDetectorRef, Directive, DestroyRef, ElementRef, inject, input, NgZone, output } from "@angular/core";
import { DashSortableService } from "../../../../modules/home/dash/service/dash.sortable.service";
import type { HomeDashPanelModel } from "../panel/home.dash.panel.model";
import { HomeDashSortableDragService } from "./home.dash.sortable.drag.service";
import type { HomeDashSortableDropEvent, HomeDashSortableDropPayload } from "./home.dash.sortable.model";

@Directive({
    selector: "[s25DashSortableList]",
    standalone: true,
})
export class HomeDashSortableListDirective {
    readonly panels = input.required<HomeDashPanelModel[]>({ alias: "s25DashSortableList" });
    readonly disabled = input(false, { alias: "s25DashSortableDisabled" });
    readonly isToolbox = input(false, { alias: "s25DashSortableToolbox" });

    readonly drop = output<HomeDashSortableDropEvent>({ alias: "s25DashSortableDrop" });

    private readonly elementRef = inject(ElementRef<HTMLElement>);
    private readonly changeDetector = inject(ChangeDetectorRef);
    private readonly destroyRef = inject(DestroyRef);
    private readonly ngZone = inject(NgZone);
    private readonly dragService = inject(HomeDashSortableDragService);
    private readonly dashSortableService = inject(DashSortableService);

    constructor() {
        const unregister = this.dragService.registerList({
            hostElement: this.elementRef.nativeElement,
            getPanels: () => this.panels(),
            getIsToolbox: () => this.isToolbox(),
            getIsDisabled: () => this.disabled(),
            applyDrop: (dropPayload) => this.ngZone.run(() => this.applyDrop(dropPayload)),
            notifyViewChanged: () => this.ngZone.run(() => this.changeDetector.markForCheck()),
        });
        this.destroyRef.onDestroy(unregister);
    }

    private applyDrop(dropPayload: HomeDashSortableDropPayload): void {
        if (dropPayload.fromIndex === dropPayload.toIndex && dropPayload.sourcePanels === dropPayload.targetPanels) {
            return;
        }

        let movedPanel: HomeDashPanelModel | undefined;
        if (dropPayload.sourcePanels === dropPayload.targetPanels) {
            const reordered = dropPayload.sourcePanels.splice(dropPayload.fromIndex, 1);
            movedPanel = reordered[0];
            if (movedPanel) {
                dropPayload.sourcePanels.splice(dropPayload.toIndex, 0, movedPanel);
            }
        } else {
            const transferred = dropPayload.sourcePanels.splice(dropPayload.fromIndex, 1);
            movedPanel = transferred[0];
            if (movedPanel) {
                dropPayload.targetPanels.splice(dropPayload.toIndex, 0, movedPanel);
            }
        }

        const toolboxDropPanelId =
            dropPayload.sourceIsToolbox && !dropPayload.targetIsToolbox && movedPanel?.panelId != null
                ? movedPanel.panelId
                : undefined;

        this.drop.emit({ positionChanged: true, toolboxDropPanelId });
        this.dashSortableService.persistOrderAfterDrop(toolboxDropPanelId);
    }
}
